import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  Option,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { v4 as uuidv4 } from "uuid";
import CustomModal from "components/common/CustomModal";
import CustomSwitch from "components/common/CustomSwitch";
import { TrashIconSvg } from "static/image/common/SvgAssets";
import { theme } from "styles/theme";
import {
  DEFAULT_REPEAT_ENTRY_TRIGGER,
  REPEAT_ENTRY_TRIGGER_LABELS,
  REPEAT_ENTRY_TRIGGERS,
  REPEAT_SET_NUMBER,
  TASK_EDITOR_REPEAT_INTERVAL_WEEKS,
  TASK_EDITOR_TASK_TYPES,
  TASK_TYPE_LABELS,
  type TaskEditorRepeatEntryTrigger,
  type TaskEditorRepeatIntervalWeeks,
  type TaskEditorTaskType,
} from "./utils/taskEditorUtils";

export interface TaskMetadataValues {
  task_id: string;
  title: string;
  set_number?: number;
  is_key_activity: boolean;
  // 활동지 유형 — 항상 명시. tool 이면 set_number 미사용(페이지가 9998 저장)·is_key_activity false 고정.
  task_type: TaskEditorTaskType;
  // tool 일 때만 — 선행 활동지 task_id (필수).
  prerequisite_task_id?: string;
  // empty(깡통) 일 때만 — 연결 툴 활동지 task_id (필수).
  linked_tool_task_id?: string;
  // repeat(반복) 일 때만 — 주기(주 단위, 1|2).
  repeat_interval_weeks?: TaskEditorRepeatIntervalWeeks;
  // repeat(반복) 일 때만 — 첫 노출 조건 트리거.
  repeat_entry_trigger?: TaskEditorRepeatEntryTrigger;
  // repeat_entry_trigger === "days_after_start" 일 때만 — 시작 후 일수.
  repeat_entry_days?: number;
  // ("set_delivered" 의 제공 세트는 별도 필드가 아니라 set_number 로 전달된다.)
}

interface TaskMetadataModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialValues?: TaskMetadataValues;
  // edit 모드에서 유형/세트 선택만 이 값으로 시작 (드래그 드롭으로 유형 전환하는 경우).
  // initialValues 는 원본 그대로 — isDirty 가 변경으로 잡혀 저장 버튼이 활성된다.
  presetTaskType?: TaskEditorTaskType;
  presetSetNumber?: number;
  existingTaskIds: string[];
  // create 모드 세트 번호 기본값 — 현재 존재하는 세트 중 최댓값(미분류 제외). 없으면 빈 값.
  defaultSetNumber?: number;
  // 이미 Key 활동지가 있는 세트 번호 (수정 중인 활동지 자신은 제외).
  // 세트당 Key 는 1개 — 이 목록의 세트에 Key 를 켜면 충돌.
  keyActivitySetNumbers: number[];
  // 피커 후보 — 전체 활동지. 선행 활동지(자기 자신 제외)·연결 툴(tool 유형만)은 모달이 거른다.
  taskCandidates: {
    task_id: string;
    title: string;
    task_type?: TaskEditorTaskType;
  }[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (values: TaskMetadataValues) => void;
  onRemove?: () => void;
}

const SHORT_ID_LENGTH = 8;
const ID_GENERATION_MAX_RETRIES = 8;

function generateShortTaskId(existingTaskIds: Set<string>): string {
  for (let attempt = 0; attempt < ID_GENERATION_MAX_RETRIES; attempt += 1) {
    const candidate = uuidv4().replace(/-/g, "").slice(0, SHORT_ID_LENGTH);
    if (!existingTaskIds.has(candidate)) {
      return candidate;
    }
  }
  // 8-hex 조합이 모두 충돌하는 일은 사실상 없음. 최후 폴백.
  return uuidv4();
}

const FIELD_HEIGHT = "44px";

const inputWrapperSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  px: "12px",
  height: FIELD_HEIGHT,
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:focus-within": {
    borderColor: theme.vars.palette.Border.Highlight,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
  },
};

const readOnlyWrapperSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  height: FIELD_HEIGHT,
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Background.Level1,
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
};

const plainInputSx = {
  flex: 1,
  fontSize: "13px",
  minHeight: 0,
  py: 0,
  backgroundColor: "transparent",
  border: "none",
  "--Input-minHeight": "0px",
  "--Input-paddingInline": "0px",
  "--Input-focusedHighlight": "transparent",
  "&::before": { display: "none" },
  "&:hover": { backgroundColor: "transparent" },
};

const formLabelSx = {
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.02em",
  color: theme.vars.palette.Content.Medium,
  mb: "6px",
};

const requiredMarkSx = {
  color: theme.vars.palette.danger[500],
  ml: "2px",
};

const keyConflictTextSx = {
  mt: "6px",
  fontSize: "11px",
  lineHeight: 1.4,
  color: theme.vars.palette.danger[500],
};

// inputWrapperSx 와 같은 외형의 Joy Select.
const selectSx = {
  height: FIELD_HEIGHT,
  minHeight: FIELD_HEIGHT,
  px: "12px",
  borderRadius: "12px",
  fontSize: "13px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  border: `1px solid ${theme.vars.palette.Border.Weak}`,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.vars.palette.Surface.Forward,
  },
};

export default function TaskMetadataModal({
  open,
  mode,
  initialValues,
  presetTaskType,
  presetSetNumber,
  existingTaskIds,
  defaultSetNumber,
  keyActivitySetNumbers,
  taskCandidates,
  isSubmitting = false,
  onClose,
  onSubmit,
  onRemove,
}: TaskMetadataModalProps) {
  const [taskId, setTaskId] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [setNumberText, setSetNumberText] = useState<string>("");
  const [isKeyActivity, setIsKeyActivity] = useState<boolean>(false);
  const [taskType, setTaskType] = useState<TaskEditorTaskType>("basic");
  const [prerequisiteTaskId, setPrerequisiteTaskId] = useState<string>("");
  const [linkedToolTaskId, setLinkedToolTaskId] = useState<string>("");
  const [repeatIntervalWeeks, setRepeatIntervalWeeks] =
    useState<TaskEditorRepeatIntervalWeeks>(2);
  const [repeatEntryTrigger, setRepeatEntryTrigger] =
    useState<TaskEditorRepeatEntryTrigger>(DEFAULT_REPEAT_ENTRY_TRIGGER);
  const [repeatEntryDaysText, setRepeatEntryDaysText] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  useEffect(() => {
    if (!showMenu) return;
    const handlePointerDown = () => setShowMenu(false);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [showMenu]);

  const existingTaskIdSet = useMemo(
    () =>
      new Set(
        existingTaskIds.map((id) => id.trim()).filter((id) => id.length > 0)
      ),
    [existingTaskIds]
  );

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialValues) {
      const initialSetNumber = presetSetNumber ?? initialValues.set_number;
      setTaskId(initialValues.task_id);
      setTitle(initialValues.title);
      setSetNumberText(
        typeof initialSetNumber === "number" ? String(initialSetNumber) : ""
      );
      setIsKeyActivity(initialValues.is_key_activity);
      setTaskType(presetTaskType ?? initialValues.task_type);
      setPrerequisiteTaskId(initialValues.prerequisite_task_id ?? "");
      setLinkedToolTaskId(initialValues.linked_tool_task_id ?? "");
      setRepeatIntervalWeeks(initialValues.repeat_interval_weeks ?? 2);
      setRepeatEntryTrigger(
        initialValues.repeat_entry_trigger ?? DEFAULT_REPEAT_ENTRY_TRIGGER
      );
      setRepeatEntryDaysText(
        typeof initialValues.repeat_entry_days === "number"
          ? String(initialValues.repeat_entry_days)
          : ""
      );
      // "세트 제공 시" 의 제공 세트는 set_number 입력(setNumberText)을 그대로 재사용한다.
      // edit 진입 시 set_number 가 위에서 이미 채워졌다 (set_delivered 면 실제 세트).
    } else {
      setTaskId(generateShortTaskId(existingTaskIdSet));
      setTitle("");
      setSetNumberText(
        typeof defaultSetNumber === "number" ? String(defaultSetNumber) : ""
      );
      setIsKeyActivity(false);
      setTaskType("basic");
      setPrerequisiteTaskId("");
      setLinkedToolTaskId("");
      setRepeatIntervalWeeks(2);
      setRepeatEntryTrigger(DEFAULT_REPEAT_ENTRY_TRIGGER);
      setRepeatEntryDaysText("");
    }
    setCopied(false);
    setConfirmingDelete(false);
    // existingTaskIdSet 은 의도적으로 의존성 제외 — 모달이 열리는 시점의 값만 사용.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialValues, presetTaskType, presetSetNumber]);

  const titleTrimmed = title.trim();
  const isToolType = taskType === "tool";
  const isEmptyType = taskType === "empty";
  // 반복 활동지는 툴처럼 세트 개념 없음 — 세트/Key 숨김, 전용 sentinel 저장, Key 불가.
  const isRepeatType = taskType === "repeat";
  // 세트 번호/Key 필드를 쓰는 유형 — basic/empty.
  const usesSetNumber = !isToolType && !isRepeatType;
  const setNumberParsed = useMemo(() => {
    const trimmed = setNumberText.trim();
    if (!trimmed) return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
    // 9997(반복)·9998(툴)·9999(미분류) 는 sentinel — 직접 입력 불가.
    if (parsed >= REPEAT_SET_NUMBER) return undefined;
    return parsed;
  }, [setNumberText]);

  // 첫 노출 "프로그램 시작 후 N일" 의 N — 1 이상 정수만 유효.
  const repeatEntryDaysParsed = useMemo(() => {
    const trimmed = repeatEntryDaysText.trim();
    if (!trimmed) return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return undefined;
    return parsed;
  }, [repeatEntryDaysText]);

  // 첫 노출 조건이 완결됐는지 — 트리거별 필수 값이 채워졌는지.
  // "세트 제공 시" 는 set_number 입력(setNumberParsed)을 그대로 쓴다.
  const isRepeatEntryValid =
    repeatEntryTrigger === "days_after_start"
      ? repeatEntryDaysParsed !== undefined
      : setNumberParsed !== undefined;

  // 선행 활동지 후보 — 자기 자신 제외, 제목 가나다순.
  const prerequisiteOptions = useMemo(
    () =>
      taskCandidates
        .filter((candidate) => candidate.task_id !== taskId)
        .sort((left, right) => left.title.localeCompare(right.title, "ko")),
    [taskCandidates, taskId]
  );

  // 연결 툴 후보 — tool 유형 활동지만, 제목 가나다순.
  const linkedToolOptions = useMemo(
    () =>
      taskCandidates
        .filter((candidate) => candidate.task_type === "tool")
        .sort((left, right) => left.title.localeCompare(right.title, "ko")),
    [taskCandidates]
  );

  // 선행 활동지(tool)·연결 툴(empty) 은 피커의 "(선택 안 함)"(= "") 으로 비워둘 수 있다.
  // 미설정은 목록의 getTaskMetaIssue ⚠ 경고로 표시되고 나중에 수정에서 연결한다.
  const isValid =
    titleTrimmed.length > 0 &&
    (isToolType ||
      (isRepeatType ? isRepeatEntryValid : setNumberParsed !== undefined));

  const isDirty = useMemo(() => {
    if (mode === "create" || !initialValues) return true;
    if (taskType !== initialValues.task_type) return true;
    if (titleTrimmed !== initialValues.title) return true;
    // tool/repeat 은 세트/Key 가 숨겨져 고정값 — 유형별 추가 필드 변경만 본다.
    if (isToolType) {
      return prerequisiteTaskId !== (initialValues.prerequisite_task_id ?? "");
    }
    if (isRepeatType) {
      return (
        repeatIntervalWeeks !== (initialValues.repeat_interval_weeks ?? 2) ||
        repeatEntryTrigger !==
          (initialValues.repeat_entry_trigger ??
            DEFAULT_REPEAT_ENTRY_TRIGGER) ||
        repeatEntryDaysParsed !== initialValues.repeat_entry_days ||
        // "세트 제공 시" 의 제공 세트는 set_number 입력으로 표현된다.
        setNumberParsed !== initialValues.set_number
      );
    }
    return (
      setNumberParsed !== initialValues.set_number ||
      isKeyActivity !== initialValues.is_key_activity ||
      (isEmptyType &&
        linkedToolTaskId !== (initialValues.linked_tool_task_id ?? ""))
    );
  }, [
    mode,
    initialValues,
    taskType,
    isToolType,
    isRepeatType,
    isEmptyType,
    prerequisiteTaskId,
    linkedToolTaskId,
    repeatIntervalWeeks,
    repeatEntryTrigger,
    repeatEntryDaysParsed,
    titleTrimmed,
    setNumberParsed,
    isKeyActivity,
  ]);

  // 세트당 Key 활동지는 1개 — Key 를 켰는데 그 세트에 이미 다른 Key 가 있으면 충돌.
  // tool/repeat 은 세트/Key 필드가 숨겨지므로 충돌 검사 대상 아님.
  const keyActivityConflict =
    usesSetNumber &&
    isKeyActivity &&
    setNumberParsed !== undefined &&
    keyActivitySetNumbers.includes(setNumberParsed);

  const canSubmit = isValid && isDirty && !keyActivityConflict;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (isToolType) {
      // 세트 개념 없음 — set_number 는 페이지가 TOOL_SET_NUMBER 로 저장, Key 는 false 고정.
      onSubmit({
        task_id: taskId,
        title: titleTrimmed,
        set_number: undefined,
        is_key_activity: false,
        task_type: "tool",
        // "(선택 안 함)" 이면 omit — 내부 모델/와이어 모두 미설정으로 유지(지연 연결).
        ...(prerequisiteTaskId
          ? { prerequisite_task_id: prerequisiteTaskId }
          : {}),
      });
      return;
    }
    if (isRepeatType) {
      // Key 는 false 고정. 첫 노출 조건에 따라 set_number 가 갈린다:
      //  - days_after_start: 세트 개념 없음 → set_number 미지정(페이지가 REPEAT_SET_NUMBER).
      //  - set_delivered: 제공 세트 = set_number → 목록에서 그 세트 섹션으로 들어간다.
      // canSubmit(isRepeatEntryValid) 보장 → 트리거별 필수 값은 채워져 있다.
      const isSetEntry = repeatEntryTrigger === "set_delivered";
      onSubmit({
        task_id: taskId,
        title: titleTrimmed,
        set_number: isSetEntry ? setNumberParsed : undefined,
        is_key_activity: false,
        task_type: "repeat",
        repeat_interval_weeks: repeatIntervalWeeks,
        repeat_entry_trigger: repeatEntryTrigger,
        ...(isSetEntry ? {} : { repeat_entry_days: repeatEntryDaysParsed }),
      });
      return;
    }
    if (setNumberParsed === undefined) return;
    onSubmit({
      task_id: taskId,
      title: titleTrimmed,
      set_number: setNumberParsed,
      is_key_activity: isKeyActivity,
      task_type: taskType,
      // empty + 연결 툴 선택 시에만 — "(선택 안 함)" 이면 omit, 그 외 유형은 full replace 로 클리어.
      ...(isEmptyType && linkedToolTaskId
        ? { linked_tool_task_id: linkedToolTaskId }
        : {}),
    });
  };

  const handleCopyTaskId = async () => {
    if (!taskId) return;
    try {
      await navigator.clipboard.writeText(taskId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 권한 없음 등 — 무시
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box component="span">
            {mode === "create" ? "새 활동지 만들기" : "활동지 메타데이터"}
          </Box>
          {mode === "edit" && onRemove && (
            <Box sx={{ position: "relative" }}>
              <IconButton
                variant="plain"
                size="sm"
                color="neutral"
                disabled={isSubmitting}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowMenu((prev) => !prev);
                }}
                sx={{
                  minHeight: "32px",
                  minWidth: "32px",
                  borderRadius: "8px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  more_vert
                </span>
              </IconButton>
              {showMenu && (
                <Box
                  onPointerDown={(event) => event.stopPropagation()}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    zIndex: 30,
                  }}
                >
                  <List
                    orientation="vertical"
                    sx={{
                      width: "max-content",
                      borderRadius: "12px",
                      border: `1px solid ${theme.vars.palette.Border.SuperWeak}`,
                      backgroundColor: theme.vars.palette.Surface.Forward,
                      boxShadow:
                        "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
                      overflow: "hidden",
                      p: 0,
                      "--List-gap": 0,
                      "--ListItem-paddingY": 0,
                    }}
                  >
                    <ListItem sx={{ p: 0 }}>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              "& svg": { width: "18px", height: "18px" },
                            }}
                          >
                            <TrashIconSvg />
                          </Box>
                        }
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowMenu(false);
                          setConfirmingDelete(true);
                        }}
                        sx={{
                          "--Button-gap": "10px",
                          width: "100%",
                          height: "40px",
                          fontSize: "14px",
                          fontWeight: 500,
                          pl: "16px",
                          pr: "20px",
                          justifyContent: "flex-start",
                          color: theme.vars.palette.Content.Strong,
                          backgroundColor: theme.vars.palette.Surface.Forward,
                        }}
                      >
                        삭제하기
                      </Button>
                    </ListItem>
                  </List>
                </Box>
              )}
            </Box>
          )}
        </Box>
      }
      subtitle={
        mode === "create"
          ? "활동지의 기본 정보를 입력해주세요."
          : "이 활동지의 메타데이터를 수정합니다."
      }
      actions={[
        {
          label: mode === "create" ? "만들기" : "저장",
          onClick: handleSubmit,
          variant: "Solid",
          color: "primary",
          buttonProps: { disabled: !canSubmit, loading: isSubmitting },
        },
        {
          label: "취소",
          onClick: onClose,
          variant: "Plain",
        },
      ]}
    >
      <Stack spacing={"16px"} sx={{ pt: "4px" }}>
        <FormControl sx={{ width: "50%" }}>
          <FormLabel sx={formLabelSx}>활동지 ID</FormLabel>
          <Box sx={readOnlyWrapperSx}>
            <Typography
              level="body-sm"
              sx={{
                flex: 1,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: "13px",
                color: theme.vars.palette.Content.Strong,
                userSelect: "all",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {taskId || "—"}
            </Typography>
            <Tooltip
              title={copied ? "복사됨" : "ID 복사"}
              size="sm"
              variant="solid"
            >
              <IconButton
                variant="plain"
                size="sm"
                onClick={handleCopyTaskId}
                sx={{
                  minHeight: "24px",
                  minWidth: "24px",
                  color: theme.vars.palette.Content.Medium,
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level2,
                    color: theme.vars.palette.Content.Strong,
                  },
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "16px" }}
                >
                  {copied ? "check" : "content_copy"}
                </span>
              </IconButton>
            </Tooltip>
          </Box>
        </FormControl>

        <FormControl>
          <FormLabel sx={formLabelSx}>
            제목
            <Box component="span" sx={requiredMarkSx}>
              *
            </Box>
          </FormLabel>
          <Box sx={inputWrapperSx}>
            <Input
              variant="plain"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="예: 우울감 알아차리기"
              autoFocus
              sx={plainInputSx}
            />
          </Box>
        </FormControl>

        <Box sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
          <FormControl sx={{ width: "33.333%" }}>
            <FormLabel sx={formLabelSx}>
              유형
              <Box component="span" sx={requiredMarkSx}>
                *
              </Box>
            </FormLabel>
            <Select<TaskEditorTaskType>
              value={taskType}
              onChange={(_, value) => {
                if (value) setTaskType(value);
              }}
              sx={selectSx}
            >
              {TASK_EDITOR_TASK_TYPES.map((type) => (
                <Option key={type} value={type}>
                  {TASK_TYPE_LABELS[type]}
                </Option>
              ))}
            </Select>
          </FormControl>

          {isToolType ? (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={formLabelSx}>
                선행 활동지
                <Box component="span" sx={requiredMarkSx}>
                  *
                </Box>
              </FormLabel>
              <Select<string>
                value={prerequisiteTaskId}
                onChange={(_, value) => setPrerequisiteTaskId(value ?? "")}
                sx={selectSx}
              >
                {prerequisiteOptions.map((candidate) => (
                  <Option key={candidate.task_id} value={candidate.task_id}>
                    {candidate.title || "(제목 없음)"}
                  </Option>
                ))}
                <Option value="">(선택 안 함)</Option>
              </Select>
            </FormControl>
          ) : isRepeatType ? (
            <FormControl sx={{ width: "33.333%" }}>
              <FormLabel sx={formLabelSx}>
                반복 주기
                <Box component="span" sx={requiredMarkSx}>
                  *
                </Box>
              </FormLabel>
              <Select<TaskEditorRepeatIntervalWeeks>
                value={repeatIntervalWeeks}
                onChange={(_, value) => {
                  if (value) setRepeatIntervalWeeks(value);
                }}
                sx={selectSx}
              >
                {TASK_EDITOR_REPEAT_INTERVAL_WEEKS.map((weeks) => (
                  <Option key={weeks} value={weeks}>
                    {weeks}주마다
                  </Option>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <FormControl sx={{ width: "33.333%" }}>
                <FormLabel sx={formLabelSx}>
                  세트 번호
                  <Box component="span" sx={requiredMarkSx}>
                    *
                  </Box>
                </FormLabel>
                <Box sx={inputWrapperSx}>
                  <Input
                    variant="plain"
                    type="number"
                    value={setNumberText}
                    onChange={(event) => setSetNumberText(event.target.value)}
                    placeholder="예: 1"
                    slotProps={{ input: { min: 1 } }}
                    sx={plainInputSx}
                  />
                </Box>
              </FormControl>

              <FormControl sx={{ width: "33.333%" }}>
                <FormLabel sx={formLabelSx}>Key 활동지</FormLabel>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pl: "4px",
                    height: FIELD_HEIGHT,
                  }}
                >
                  <CustomSwitch
                    checked={isKeyActivity}
                    onChange={(event) => setIsKeyActivity(event.target.checked)}
                  />
                </Box>
                {keyActivityConflict && (
                  <Typography sx={keyConflictTextSx}>
                    이 세트엔 이미 Key 활동지가 있어요
                  </Typography>
                )}
              </FormControl>
            </>
          )}
        </Box>

        {isRepeatType && (
          <Box sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={formLabelSx}>
                첫 노출 조건
                <Box component="span" sx={requiredMarkSx}>
                  *
                </Box>
              </FormLabel>
              <Select<TaskEditorRepeatEntryTrigger>
                value={repeatEntryTrigger}
                onChange={(_, value) => {
                  if (value) setRepeatEntryTrigger(value);
                }}
                sx={selectSx}
              >
                {REPEAT_ENTRY_TRIGGERS.map((trigger) => (
                  <Option key={trigger} value={trigger}>
                    {REPEAT_ENTRY_TRIGGER_LABELS[trigger]}
                  </Option>
                ))}
              </Select>
            </FormControl>

            {repeatEntryTrigger === "days_after_start" ? (
              <FormControl sx={{ width: "40%" }}>
                <FormLabel sx={formLabelSx}>
                  프로그램 시작 후
                  <Box component="span" sx={requiredMarkSx}>
                    *
                  </Box>
                </FormLabel>
                <Box sx={inputWrapperSx}>
                  <Input
                    variant="plain"
                    type="number"
                    value={repeatEntryDaysText}
                    onChange={(event) =>
                      setRepeatEntryDaysText(event.target.value)
                    }
                    placeholder="예: 14"
                    slotProps={{ input: { min: 1 } }}
                    sx={plainInputSx}
                  />
                  <Typography
                    level="body-sm"
                    sx={{
                      fontSize: "13px",
                      color: theme.vars.palette.Content.Medium,
                      whiteSpace: "nowrap",
                    }}
                  >
                    일 뒤
                  </Typography>
                </Box>
              </FormControl>
            ) : (
              <FormControl sx={{ width: "40%" }}>
                <FormLabel sx={formLabelSx}>
                  세트 번호
                  <Box component="span" sx={requiredMarkSx}>
                    *
                  </Box>
                </FormLabel>
                {/* "세트 제공 시" 의 세트 = 활동지의 set_number — 세트 번호 필드와 동일한 숫자 입력. */}
                <Box sx={inputWrapperSx}>
                  <Input
                    variant="plain"
                    type="number"
                    value={setNumberText}
                    onChange={(event) => setSetNumberText(event.target.value)}
                    placeholder="예: 1"
                    slotProps={{ input: { min: 1 } }}
                    sx={plainInputSx}
                  />
                </Box>
              </FormControl>
            )}
          </Box>
        )}

        {isEmptyType && (
          <FormControl sx={{ width: "66.667%" }}>
            <FormLabel sx={formLabelSx}>
              연결 툴
              <Box component="span" sx={requiredMarkSx}>
                *
              </Box>
            </FormLabel>
            <Select<string>
              value={linkedToolTaskId}
              onChange={(_, value) => setLinkedToolTaskId(value ?? "")}
              sx={selectSx}
            >
              {linkedToolOptions.map((candidate) => (
                <Option key={candidate.task_id} value={candidate.task_id}>
                  {candidate.title || "(제목 없음)"}
                </Option>
              ))}
              <Option value="">(선택 안 함)</Option>
            </Select>
          </FormControl>
        )}
      </Stack>

      <CustomModal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title="활동지를 삭제하시겠어요?"
        subtitle="이 작업은 되돌릴 수 없습니다."
        actions={[
          {
            label: "삭제",
            onClick: () => {
              setConfirmingDelete(false);
              onRemove?.();
            },
            variant: "Solid",
            color: "danger",
            buttonProps: { loading: isSubmitting },
          },
          {
            label: "취소",
            onClick: () => setConfirmingDelete(false),
            variant: "Plain",
          },
        ]}
      />
    </CustomModal>
  );
}
